import React, { useState, useEffect } from "react";
import Highcharts from "highcharts/highstock";
import { useVsiAppContext } from "../stores/VsiApp";
import styles from "../utils/themeStyles";
import { genericColors } from "../utils/chartUtils";

import ThemedChartLight from "./ThemedChartLight";
import ThemedChartDark from "./ThemedChartDark";
import { ChartArea } from "../styled/Stockchart.styled";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

require("highcharts/modules/variwide")(Highcharts);
require("highcharts/indicators/indicators")(Highcharts);
require("highcharts/indicators/pivot-points")(Highcharts);
require("highcharts/indicators/macd")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/highcharts-more")(Highcharts);
require("highcharts/modules/gantt")(Highcharts);
require("highcharts/modules/drilldown")(Highcharts);

function FlexChart({ categories, config, series, data, navDisabled, title, height, events, yAxisTitle, constructorType, disableRangeSelector,type }) {
	const { theme, isTabletScreen } = useVsiAppContext();
	const basicTextStyle = {
		color: styles[theme].textColor,
		fontFamily: styles[theme].fontFamily,
	};
	
	const updateSeries = (series, data) => {
		if (series) return series;
		else if (data)
			return [
				{
					data,
				},
			];
		return [];
	};
	
	const handleYAxis = config => {
		const defaultYAxis = {
			title: {
				enabled: true,
				text: yAxisTitle,
				style: {
					...basicTextStyle,
					fontSize: '115%',
					fontWeight: '600'
				}
			},
			opposite: false,
			labels: {
				style: basicTextStyle,
			},
		};
		if (!config) return defaultYAxis;
		else if (typeof config?.yAxis === "object" && !Array.isArray(config?.yAxis)) {
			return {
				...defaultYAxis,
				...config.yAxis,
				title: {
				  ...config.yAxis.title,
				  style: defaultYAxis.title.style
				}
			}
		} else if (Array.isArray(config?.yAxis)) {
			return config.yAxis.map(axis => ({
				...defaultYAxis,
				...axis,
				title: {
				  ...axis.title,
				  style: defaultYAxis.title.style
				}
			}))
		}
	};
	
	const [chartOptions, setChartOptions] = useState({
		colors: config?.colors || genericColors,
		credits: {
			enabled: false,
		},
		mapNavigation: {
			enabled: false
		},
		rangeSelector: {
			enabled: !disableRangeSelector,
		},
		navigator: {
			enabled: navDisabled ? false : true
		},
		tooltip: {
			backgroundColor: styles[theme].altBgColor,
			style: basicTextStyle,
		},
		plotOptions: {
			series: {
				stacking: "normal",
				dataLabels: {
				  enabled: true,
				  color: basicTextStyle.color
				},
				...config?.plotOptions?.series,
			}
		},
		xAxis: {
			categories: categories || null,
			// tickInterval: 30 * 24, // one week
			labels: {
				style: basicTextStyle,
			},
			// minRange: 100
		},
		yAxis: handleYAxis(config),
		chart: {
			type,
			backgroundColor: "transparent",
			color: styles[theme].textColor,
			height,
			zoomType: 'x',
			events,
			zooming: {
				mouseWheel: {
					enabled: false
				}
			}
		},
		title: {
			title: {
				style: basicTextStyle,
			},
			text: title ? title : null,
			style: basicTextStyle,
		},
		labels: {
			style: basicTextStyle,
		},
		legend: {
			itemStyle: basicTextStyle,
			borderColor: 'transparent',
			align: "center",
			enabled: true,
		},
		series: updateSeries(series, data),
	});
	
	useEffect(() => {
		const basicTextStyle = {
			color: styles[theme].textColor,
			fontFamily: styles[theme].fontFamily,
		};
		setChartOptions({
			...chartOptions,
			colors: config?.colors || genericColors,
			credits: {
				enabled: false,
			},
			mapNavigation: {
				enabled: false
			},
			navigator: {
				enabled: !navDisabled
			},
			tooltip: {
				backgroundColor: styles[theme].altBgColor,
				style: basicTextStyle,
			},
			plotOptions: {
				series: {
					...chartOptions?.plotOptions,
					stacking: "normal",
					dataLabels: {
					  ...chartOptions?.plotOptions?.series?.dataLabels,
					  color: basicTextStyle.color
					},
					...config?.plotOptions?.series,
				}
			},
			xAxis: {
				categories: categories || null,
				// tickInterval: 30 * 24, // one week
				labels: {
					style: basicTextStyle,
				},
				// minRange: 100
			},
			yAxis: handleYAxis(config),
			chart: {
				...chartOptions.chart,
				type,
				backgroundColor: "transparent",
				color: styles[theme].textColor,
				height,
				zoomType: 'x',
				events,
			},
			title: {
				title: {
					style: basicTextStyle,
				},
				text: title ? title : null,
				style: basicTextStyle,
			},
			labels: {
				style: basicTextStyle,
			},
			legend: {
				itemStyle: basicTextStyle,
				borderColor: 'transparent',
				align: "center",
				enabled: true,
			},
			rangeSelector: {
				enabled: !disableRangeSelector,
				buttonTheme: {
				  fill: styles[theme].sideBgColor,
				  style: {
					color: styles[theme].textColor
				  },
				  states: {
					hover: {
					  fill: styles[theme].navItemBgActive
					},
					select: {
					  fill: styles[theme].navItemBgActive,
					  style: {
						color: styles[theme].textColor
					  }
					},
					disabled: {
					  fill: styles[theme].disabledBtnColor,
					  style: {
						cursor: 'auto'
					  }
					}
				  }
				},
				inputStyle: {
				  color: basicTextStyle.color
				},
				labelStyle: {
				  color: basicTextStyle.color
				}
			},
			series: updateSeries(series, data),
		});
	}, [categories, series, data, theme, isTabletScreen, navDisabled, disableRangeSelector, events, type]);
	
	return <ChartArea>
		{"light" ? (
			<ThemedChartLight options={chartOptions} constructorType={constructorType || 'stockChart'}/>
		): (
			<ThemedChartDark options={chartOptions} constructorType={constructorType || 'stockChart'}/>
		)}
	</ChartArea>;
}

FlexChart.displayName = "FlexChart"

export default withErrorBoundary(FlexChart, GeneralErrorBoundary);
